import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setSkipModalData } from "../../redux/onboardingReducer";
import { getOnboardingDetails, onboardingQuestions, setDataForStates, setOnboardingData, createPlaidLinkToken, exchangePlaidPublicToken } from "../../shared/_services/common.services";
import Loader from "../Loader";
import { useSnackbar } from "notistack";
import AIChatBot from "./AIChatBot";

import { usePlaidLink } from "react-plaid-link";
import PlaidFallbackModal from "../PlaidFallbackModal";

const QuestionSix = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [optionAmounts, setOptionAmounts] = useState({});
  const [allQuestions, setAllQuestions] = useState([]);
  const [showLoaderFive, setShowLoaderFive] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [showPlaidModal, setShowPlaidModal] = useState(false);
  const [modalVariant, setModalVariant] = useState('success');
  const [modalMessage, setModalMessage] = useState('');
  const [modalSecondaryMessage, setModalSecondaryMessage] = useState('');
  // Plaid state
  const [linkToken, setLinkToken] = useState(null);
  const token = JSON.parse(localStorage.getItem("currentUser"))?.token;
  const userId = JSON.parse(localStorage.getItem("currentUser"))?.user?._id;

  const isFromUserQuestionnaire = location?.state?.fromUserQuestionnaire;
  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  const subscribed = !!currentUser?.user?.selected_plan;
  const payment_status = currentUser?.user?.payment_status;

  useEffect(() => {
    if (!token || !userId) return;

    createPlaidLinkToken(userId, `${process.env.REACT_APP_URL}/q6`)
      .then((link_token) => setLinkToken(link_token))
      .catch((error) => {
        console.error("Error creating Plaid link token:", error);
        setModalVariant('error');
        setModalMessage('We couldn’t connect your accounts.');
        setModalSecondaryMessage('This happens sometimes with bank security settings. You can try again, or enter things manually — whatever you prefer.');
        setShowPlaidModal(true);
      });
  }, [token, userId]);

  // Plaid Link configuration
  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (public_token) => {
      try {
        setShowLoaderFive(true);
        await exchangePlaidPublicToken(public_token, userId);
        setModalVariant('info');
        setModalMessage('Connecting your accounts…');
        setModalSecondaryMessage('This usually takes just a few seconds.\nYour information is encrypted and secure.');
        setShowPlaidModal(true);
        // You might want to refresh data or update state here

        // Step 4: Refresh onboarding data after successful Plaid connection
        await checkAndFillDataForQuesFive();

        // Update modal to success
        setModalVariant('success');
        setModalMessage('Your accounts are connected!');
        setModalSecondaryMessage('We pulled in the details we could. You can add or edit anything manually — estimates are totally fine.');

      } catch (error) {
        console.error("Error exchanging Plaid token:", error);
        setModalVariant('error');
        setModalMessage('We couldn’t connect your accounts.');
        setModalSecondaryMessage('This happens sometimes with bank security settings. You can try again, or enter things manually — whatever you prefer.');
        setShowPlaidModal(true);
      } finally {
        setShowLoaderFive(false);
      }
    },
    onExit: (error) => {
      if (error) {
        console.error("Plaid link error:", error);
        setModalVariant('warning');
        setModalMessage('Your bank didn’t authorize the connection.');
        setModalSecondaryMessage('This is common if your bank requires extra verification. You can try again, or enter your info manually — your advisor can help.');
        setShowPlaidModal(true);
      }
    },
  });


  const checkAndFillDataForQuesFive = async () => {
    setShowLoaderFive(true)
    const questions = await onboardingQuestions("Investments");
    // #69: an API failure leaves `.data` undefined — indexing `allQuestions[0]`
    // during render then throws (optional chaining doesn't guard the index
    // itself), which unmounts the whole app with no error boundary to catch
    // it below this point. A malformed 200 body can also leave `.data`
    // non-array-but-truthy, which `?? []` alone wouldn't catch. Falling back
    // to [] keeps this a normal "no data" render instead of a crash.
    setAllQuestions(Array.isArray(questions?.data) ? questions.data : []);
    if (questions.status === 200) {
      const getOnboardingDetailsData = await getOnboardingDetails(questions?.data[0]?._id);
      if (getOnboardingDetailsData.status === 200) {
        let onboardingData = null;
        if (Array.isArray(getOnboardingDetailsData?.data?.data)) {
          onboardingData = getOnboardingDetailsData?.data?.data[0];
        } else if (getOnboardingDetailsData?.data?.error) {
          onboardingData = [];
        }
        let temp = setDataForStates(questions.data[0], onboardingData, true);
        if (temp.selectedData.length > 0) {
          let data2 = {};
          let data1 = temp.selectedData.map((data) => {
            data2[data.name] = data.value;
            return data.name;
          });
          setSelectedOptions([...data1]);
          setOptionAmounts({ ...data2 })
        }
      }
    }
    setShowLoaderFive(false)
  }


  useEffect(() => {
    checkAndFillDataForQuesFive();
  }, []);

  const handleNextQuestion = async () => {
    setShowLoaderFive(true)
    // Check if any selected option has an empty or null value
    const hasInvalidValue = selectedOptions.some(option => !optionAmounts[option]);

    if (hasInvalidValue && subscribed) {
      enqueueSnackbar("Please enter a value for selected options or unselect", { variant: "error" });
      setShowLoaderFive(false);
      return;
    }

    let body = {};
    
    let currentUser = JSON.parse(localStorage.getItem('currentUser'));
    body.clientId = currentUser?.user?._id;
    body.questionId = allQuestions[0]?._id;
    body.questionName = allQuestions[0]?.questionName;
    let selectedData = selectedOptions.map((data) => ({
      "name": data,
      "value": optionAmounts[data] || ""
    }));
    let oldData = selectedOptions.map((data) => ({
      "name": data,
      "value": parseFloat(optionAmounts[data]) || 0  // Convert to float, default to 0 if inval_id
    }));

    let totalAssetsAmt = oldData.reduce((total, option) => total + option.value, 0);
    body.totalAssets = totalAssetsAmt;
    body = { ...body, selectedOptions: selectedData };
    
    if (isFromUserQuestionnaire) {
      let updateResponse = await setOnboardingData(body);
      setShowLoaderFive(false)
      if (updateResponse.status === 201) {
        navigate('/questionnaire');
      }
    } else {
      if (selectedOptions.length > 0) {
        let data = await setOnboardingData(body);
        setShowLoaderFive(false)
        if (data.status === 201) {
          navigate('/q7');
        }
      } else {
        dispatch(setSkipModalData(true));
      }
    }
  };


  const toggleOption = (option) => {
    setSelectedOptions((prevSelectedOptions) => {
      if (prevSelectedOptions.includes(option)) {
        return prevSelectedOptions.filter((item) => item !== option);
      } else {
        return [...prevSelectedOptions, option];
      }
    });
  };

  const handleAmountChange = (option, amount) => {
    if (/^\d{0,10}(\.\d{0,2})?$/.test(amount)) {
      setOptionAmounts({ ...optionAmounts, [option]: amount });
    } else {
      enqueueSnackbar("Amount should not exceed 10 digits.", { variant: "error" });
    }
  };

  return (
    <>
      {/* Question Five */}
      {showLoaderFive ? (
        <Loader />
      ) : (
        <div className="question-set">
          <div className="onboaring-quesn justify-content-between d-flex place-items-center">
            <h2>
              <span>Q6</span>
              <div>
                Want to give your advisor a quick snapshot of your money?
                <br />
                <p>Totally optional. Estimates are great. Your advisor can help fill this in later over chat.</p>
              </div>
            </h2>
            {!isFromUserQuestionnaire && (
              <button
                type="button"
                className="as-btn-secondary"
                onClick={() => { navigate('/q7') }}
              >
                Continue Later</button>)}
          </div>

          {/* Plaid Connect Button for Investments */}
          <div className="mb-4 mt-3">
            <button
              className="as-btn-primary px-4 py-2"
              onClick={() => {
                // payment_status is a boolean on the backend (model/user.js);
                // this used to compare against the string "false", which a
                // boolean never equals, so a lapsed subscriber never got
                // routed to /package.
                if (!payment_status) {
                  navigate("/package", { state: { from: "/q6" } });
                } else {
                  open();
                }
              }}
              disabled={!ready || showLoaderFive}
            >
              <i className="fa fa-bank mr-2" aria-hidden="true"></i>
              {showLoaderFive ? "Connecting..." : "Connect Investment Accounts with Plaid"}
            </button>
            <p className="text-muted small mt-2">
              If you’re unsure, skip this step. Your advisor will walk through it with you.
            </p>
          </div>

          <div className="onboarding-label mb-4">
            <h5 className="mb-0">Add your accounts manually</h5>
            <p className="text-muted small">
              No need to be exact — estimates are perfectly fine. Your advisor will help you fill in the details.”
            </p>
          </div>

          <div className={`input-multi-select ${!subscribed ? "input-multi-select-disabled" : ""}`} >
            {allQuestions[0]?.options?.map((option) => (
              <div
                key={option}
                className={`input-multi-select-option ${selectedOptions.includes(option) ? "if_selected" : ""
                  }`}
                onClick={() => toggleOption(option)}
              >
                <span>{option}</span>
                {subscribed && (
                  <input
                    placeholder="-Enter Amount-"
                    type="number"
                    value={optionAmounts[option] || ""}
                    onChange={(e) => handleAmountChange(option, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </div>
            ))}
          </div>
        </div>)}

      {/* Navigation buttons */}
      {!showLoaderFive && (
        <div className="onboarding-footer">
          <div className="onboarding-footer-btns">
            <div className="d-flex justify-content-between w-100">
              {isFromUserQuestionnaire ? (
                <>
                  <button
                    type="button"
                    className="as-btn-secondary"
                    onClick={() => {
                      navigate("/questionnaire")
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="as-btn-primary"
                    onClick={handleNextQuestion}
                  >
                    Update
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="as-btn-secondary"
                    onClick={() => navigate("/q5")}
                  >
                    ← Money Snapshot (Debts)
                  </button>
                  <button
                    type="button"
                    className="as-btn-primary"
                    onClick={handleNextQuestion}
                  >
                    About You →
                  </button>
                </>
              )}
            </div>
            <AIChatBot questionName={allQuestions[0]?.questionName}
              options={allQuestions[0]?.options} firstMsg="I’m here to help you think about investment allocation. What’s your experience with investing, and what are your goals for growing your wealth?" />
          </div>
        </div>

      )}
      {showPlaidModal && (
        <PlaidFallbackModal variant={modalVariant} message={modalMessage} secondaryMessage={modalSecondaryMessage} onClose={() => setShowPlaidModal(false)} onTryAgain={open} />
      )}
    </>
  );
};

export default QuestionSix;
